/* JPI collection-card purchase actions. Additive; Ride card markup remains untouched. */
(() => {
  const ROOT_SELECTOR = '#MainContent[data-template="collection"]';
  const CARD_SELECTOR = '#product-grid .product-card-wrapper';
  const productCache = new Map();
  let scanQueued = false;

  function getHeader() {
    return document.querySelector('jpi-universal-header');
  }

  function getLabels() {
    const data = getHeader()?.dataset || {};
    return {
      add: data.jpiCollectionAddLabel || 'Add to cart',
      adding: data.jpiCollectionAddingLabel || 'Adding…',
      added: data.jpiCollectionAddedLabel || 'Added',
      choose: data.jpiCollectionChooseLabel || 'Choose options',
      soldOut: data.jpiCollectionSoldOutLabel || 'Sold out',
      error: data.jpiCollectionErrorLabel || 'Try again',
      addedStatus: data.jpiCollectionAddedStatus || 'Product added to cart.',
      errorStatus: data.jpiCollectionErrorStatus || 'This product could not be added. Please try again.',
      cartItems: data.jpiCartItemsLabel || 'items in cart',
    };
  }

  function getProductLink(card) {
    return card.querySelector('.card__heading a[id^="CardLink-"]') || card.querySelector('.card__heading a[href*="/products/"]');
  }

  function getProductJsonUrl(productLink) {
    const url = new URL(productLink.href, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.href;
  }

  async function loadProduct(productLink) {
    const productJsonUrl = getProductJsonUrl(productLink);
    if (!productCache.has(productJsonUrl)) {
      productCache.set(productJsonUrl, fetch(productJsonUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      }));
    }
    return productCache.get(productJsonUrl);
  }

  function createStatus() {
    const status = document.createElement('span');
    status.className = 'jpi-collection-action__status jpi-visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    return status;
  }

  function createActionShell(card) {
    const content = card.querySelector('.card > .card__content');
    if (!content) return null;

    const shell = document.createElement('div');
    shell.className = 'jpi-collection-action';
    shell.hidden = true;
    shell.append(createStatus());
    content.append(shell);
    return shell;
  }

  function cartJsonUrl() {
    const cartLink = getHeader()?.querySelector('a[href*="/cart"]');
    const url = new URL(cartLink?.href || '/cart', window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.href;
  }

  async function refreshCartCount(labels) {
    const response = await fetch(cartJsonUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;

    const cart = await response.json();
    getHeader()?.querySelectorAll('a[href*="/cart"]').forEach((cartLink) => {
      let count = cartLink.querySelector('.jpi-header__cart-count');
      if (cart.item_count > 0 && !count) {
        count = document.createElement('span');
        count.className = 'jpi-header__cart-count';
        count.setAttribute('aria-hidden', 'true');
        cartLink.append(count);
      }
      if (count) {
        count.textContent = String(cart.item_count);
        count.hidden = cart.item_count < 1;
      }

      if (!cartLink.dataset.jpiBaseCartLabel) {
        cartLink.dataset.jpiBaseCartLabel = cartLink.getAttribute('aria-label') || 'Cart';
      }
      cartLink.setAttribute(
        'aria-label',
        cart.item_count > 0
          ? `${cartLink.dataset.jpiBaseCartLabel}, ${cart.item_count} ${labels.cartItems}`
          : cartLink.dataset.jpiBaseCartLabel,
      );
    });
  }

  async function addVariant(button, variantId, status) {
    const labels = getLabels();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    button.textContent = labels.adding;
    status.textContent = '';

    try {
      const addUrl = `${(window.routes?.cart_add_url || '/cart/add').replace(/\/$/, '')}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Cart request failed');

      button.textContent = labels.added;
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      status.textContent = labels.addedStatus;
      await refreshCartCount(labels);
      window.setTimeout(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('is-added');
        button.textContent = labels.add;
      }, 1600);
    } catch (error) {
      console.error('[JPI collection action]', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading', 'is-added');
      button.textContent = labels.error;
      status.textContent = labels.errorStatus;
    }
  }

  function renderAction(card, shell, product, productLink) {
    const labels = getLabels();
    const status = shell.querySelector('.jpi-collection-action__status');
    const availableVariants = Array.isArray(product.variants)
      ? product.variants.filter((variant) => variant.available)
      : [];
    const isSingleVariant = Array.isArray(product.variants) && product.variants.length === 1;

    let action;
    if (!product.available || availableVariants.length === 0) {
      action = document.createElement('button');
      action.type = 'button';
      action.disabled = true;
      action.textContent = labels.soldOut;
      action.className = 'jpi-collection-action__button is-disabled';
    } else if (isSingleVariant) {
      action = document.createElement('button');
      action.type = 'button';
      action.textContent = labels.add;
      action.className = 'jpi-collection-action__button';
      action.dataset.variantId = String(availableVariants[0].id);
      action.addEventListener('click', () => addVariant(action, availableVariants[0].id, status));
    } else {
      action = document.createElement('a');
      action.href = productLink.href;
      action.textContent = labels.choose;
      action.className = 'jpi-collection-action__button';
    }

    shell.prepend(action);
    shell.hidden = false;
    card.querySelector('.card')?.classList.add('jpi-collection-card--actions');
  }

  async function enhanceCard(card) {
    if (card.dataset.jpiCollectionActionState) return;
    card.dataset.jpiCollectionActionState = 'loading';
    const productLink = getProductLink(card);
    const shell = productLink ? createActionShell(card) : null;
    if (!productLink || !shell) {
      card.dataset.jpiCollectionActionState = 'unavailable';
      return;
    }

    try {
      const product = await loadProduct(productLink);
      renderAction(card, shell, product, productLink);
      card.dataset.jpiCollectionActionState = 'ready';
    } catch (error) {
      console.error('[JPI collection action]', error);
      shell.remove();
      card.dataset.jpiCollectionActionState = 'error';
    }
  }

  function scan() {
    scanQueued = false;
    if (!document.querySelector(ROOT_SELECTOR)) return;
    document.querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(scan);
  }

  function start() {
    scan();
    const gridContainer = document.querySelector('#ProductGridContainer');
    if (!gridContainer) return;
    new MutationObserver(scheduleScan).observe(gridContainer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
